import {
  GetProviderName,
  GPUInstanceType,
} from "types/InstanceTypes";
import { displayPlatformType } from "components/Environment/utils";
import React, { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  filterForGPUInstances,
  formatGPUName,
} from "components/Environment/Create/EnvironmentCreatePublic";
import { useQuery } from "@tanstack/react-query";
import {
  RadioCardGroup,
  RadioCardItem,
} from "components/Graphs/RadioCardGroup";
import { CreateFirewallRule } from "components/Environment/Create/ExposePorts";
import { getDefaultStorageGiB } from "utils/instanceTypes";
import {
  getInstanceSelectionKey,
  getProviderSwitchInstances,
} from "./providerSwitching";
import {
  publicInstanceTypesQueryOptions,
  useInstanceTypeQuery,
} from "server/devplane/instanceTypes/queries";
import { parseInstanceType } from "utils/instanceTypes";

interface LaunchableMultiComputeSelectorProps {
  currentInstanceTypeObject: GPUInstanceType | null;
  intendedInstanceType: string | null;
  intendedDiskStorage: string | null;
  intendedCloudCredId: string | null;
  setInstanceObject?: (
    instanceObject: GPUInstanceType | null,
    diskStorage: string,
  ) => void;
  intendedFirewallRules: CreateFirewallRule[] | null;
  onIsOutOfStock?: (isOutOfStock: boolean) => void;
  onProviderDifferenceChange?: (isDifferent: boolean) => void;
  allowProviderSwitching?: boolean;
}

export const LaunchableMultiComputeSelector: React.FC<
  LaunchableMultiComputeSelectorProps
> = ({
  currentInstanceTypeObject,
  intendedInstanceType,
  setInstanceObject,
  intendedCloudCredId,
  intendedDiskStorage,
  intendedFirewallRules,
  onIsOutOfStock,
  onProviderDifferenceChange,
  allowProviderSwitching = true,
}) => {
  const selectedInstance = getInstanceSelectionKey(currentInstanceTypeObject);
  const { data: instancesWithErrors, isInitialLoading: isInstancesLoading } =
    useQuery(publicInstanceTypesQueryOptions());

  const {
    data: intendedInstance,
    isInitialLoading: loadingIntendedInstanceTypeObject,
  } = useInstanceTypeQuery(
    parseInstanceType(intendedInstanceType)?.instanceType ?? "",
    intendedCloudCredId ?? "",
  );

  const intendedInstanceTypeObject = useMemo<GPUInstanceType | null>(() => {
    if (!intendedInstance) return null;
    return {
      ...intendedInstance,
      supported_gpus:
        intendedInstance.supported_gpus?.map((gpu) => ({
          ...gpu,
          name: formatGPUName(gpu.name),
        })) ?? [],
    };
  }, [intendedInstance]);

  const allInstances = useMemo(
    () => instancesWithErrors?.allInstanceTypes ?? [],
    [instancesWithErrors],
  );

  const allGPUInstances = useMemo(
    () =>
      instancesWithErrors
        ? filterForGPUInstances(
            instancesWithErrors.allInstanceTypes,
            false,
          ).filter(
            (instance) =>
              !instance.supported_gpus[0].name.includes("5090") &&
              !instance.supported_gpus[0].name.includes("4090"),
          )
        : [],
    [instancesWithErrors],
  );

  const isCPUInstance = (instance: GPUInstanceType) =>
    !instance.supported_gpus || instance.supported_gpus.length === 0;

  const relevantInstances = useMemo(() => {
    if (!intendedInstanceTypeObject) {
      return [];
    }

    const containsFirewallRules =
      intendedFirewallRules && intendedFirewallRules.length > 0;

    let matchingInstances: GPUInstanceType[];

    if (isCPUInstance(intendedInstanceTypeObject)) {
      // For CPU instances, match other CPU instances from allInstances
      matchingInstances = allInstances.filter(
        (instance) =>
          isCPUInstance(instance) &&
          (!containsFirewallRules || instance.can_modify_firewall_rules),
      );
    } else {
      const targetGPUName = intendedInstanceTypeObject.supported_gpus[0].name;
      const targetGPUCount = intendedInstanceTypeObject.supported_gpus[0].count;

      // Filter for instances with same GPU name and count
      matchingInstances = allGPUInstances.filter(
        (instance) =>
          instance.supported_gpus[0].name === targetGPUName &&
          instance.supported_gpus[0].count === targetGPUCount &&
          (!containsFirewallRules || instance.can_modify_firewall_rules),
      );
    }

    // Remove instances that are the same provider as the intended instance type
    const filteredInstances = matchingInstances.filter(
      (instance) => instance.cloud !== intendedInstanceTypeObject.cloud,
    );

    const seenProviderCloud = new Set<string>();
    // Remove duplicates based on provider/cloud combination
    const uniqueInstances = filteredInstances.filter((instance) => {
      const key = `${instance.provider || "none"}-${instance.cloud || "none"}`;
      if (seenProviderCloud.has(key)) {
        return false;
      }
      seenProviderCloud.add(key);
      return true;
    });

    return uniqueInstances;
  }, [
    allGPUInstances,
    allInstances,
    intendedFirewallRules,
    intendedInstanceTypeObject,
  ]);

  // Only show available instances the user can switch to. The current
  // provider remains part of the selected instance summary above this row.
  const displayInstances = useMemo(
    () =>
      getProviderSwitchInstances({
        allowProviderSwitching,
        allInstances,
        relevantInstances,
        intendedInstanceTypeObject,
        currentInstanceTypeObject,
      }),
    [
      allowProviderSwitching,
      allInstances,
      relevantInstances,
      intendedInstanceTypeObject,
      currentInstanceTypeObject,
    ],
  );

  const handleInstanceSelect = (instanceKey: string) => {
    if (setInstanceObject) {
      const instance =
        allGPUInstances.find(
          (candidate) => getInstanceSelectionKey(candidate) === instanceKey,
        ) ||
        allInstances.find(
          (candidate) => getInstanceSelectionKey(candidate) === instanceKey,
        );
      if (instance) {
        const diskStorage = instance.elastic_root_volume
          ? intendedDiskStorage ||
            getDefaultStorageGiB(instance.supported_storage)
          : instance.supported_storage && instance.supported_storage.length > 0
            ? instance.supported_storage[0].size
            : intendedDiskStorage ||
              getDefaultStorageGiB(instance.supported_storage);
        setInstanceObject(instance, diskStorage);
      }
    }
  };

  const resolved = !isInstancesLoading && !loadingIntendedInstanceTypeObject;

  const isCurrentInstanceOutOfStock = useMemo(() => {
    if (!resolved || !currentInstanceTypeObject) return false;
    const currentInstanceKey = getInstanceSelectionKey(
      currentInstanceTypeObject,
    );
    return !allInstances.some(
      (instance) => getInstanceSelectionKey(instance) === currentInstanceKey,
    );
  }, [resolved, currentInstanceTypeObject, allInstances]);
  const isNonIntendedCloudSelected =
    allowProviderSwitching &&
    resolved &&
    !!intendedInstanceTypeObject &&
    !!currentInstanceTypeObject &&
    GetProviderName(currentInstanceTypeObject) !==
      GetProviderName(intendedInstanceTypeObject);

  useEffect(() => {
    if (resolved) {
      onIsOutOfStock?.(isCurrentInstanceOutOfStock);
    }
  }, [resolved, isCurrentInstanceOutOfStock, onIsOutOfStock]);

  useEffect(() => {
    if (resolved) {
      onProviderDifferenceChange?.(isNonIntendedCloudSelected);
    }
  }, [resolved, isNonIntendedCloudSelected, onProviderDifferenceChange]);

  if (!allowProviderSwitching || !resolved || displayInstances.length === 0) {
    return null;
  }

  const providerPillClass =
    "!w-auto !rounded-full !px-2 !py-1 text-[11px] font-medium leading-none shadow-none";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
        <p className="shrink-0 text-sm font-medium text-secondary">
          Switch provider
        </p>
        <div className="flex flex-wrap items-center justify-start gap-1">
          <motion.div
            className="flex flex-wrap items-center justify-start gap-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <RadioCardGroup
              value={selectedInstance}
              onValueChange={handleInstanceSelect}
              className="!flex !flex-wrap !justify-start !gap-1"
            >
              {displayInstances.map((instance) => (
                <motion.div
                  key={getInstanceSelectionKey(instance)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <RadioCardItem
                    value={getInstanceSelectionKey(instance)}
                    className={providerPillClass}
                  >
                    <span className="font-medium text-primary">
                      {displayPlatformType(GetProviderName(instance) || "")}
                    </span>
                  </RadioCardItem>
                </motion.div>
              ))}
            </RadioCardGroup>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
